/**
 * The chart for editing a position in its own row, as a pure function: the rules of the row, and nothing else.
 *
 * The row is three files, each with one job:
 *
 * - This file says what happens. A state and an event go in through {@link transition}, and the next state and the effects to carry out come back. The state says whether the row is reading or editing, and in edit mode what the user has done to each field. The effects say where the focus goes, which fields are committed,
 *   whether the key's default is prevented and whether the selection toggles. No DOM, no Effect and no timers.
 * - Position-item-renderer.ts makes it happen. The renderer is a thin shell around the chart: it turns DOM events into the events below, runs them through {@link transition} and carries out the effects on the real row — shows the fields or the labels, moves the focus, dispatches the change. It keeps no rule of its own
 *   about when the row edits, commits or lets go.
 * - Position-item-renderer.chart.world.ts is everything the row does not control, and is for the tests only: where the focus is, whether the window has it, what the list hands the row, and every action the user and the browser can take. It turns an action into the events the shell would send for it.
 *
 * Why three. Editing in a row is focus and keys meeting each other — F2, Enter, Escape, Tab round the fields, a click elsewhere, a price landing mid-edit, a pooled row coming back — and the cases multiply faster than tests written one at a time can keep up with. Free of the DOM, the rules can be walked exhaustively in
 * milliseconds: position-item-renderer.chart.test.ts reaches every state the chart and the world reach together and checks the invariants in each, failing with the shortest trace that breaks one. That proves the rules. The conformance test in position-item-renderer.test.ts proves the shell follows them: it takes the
 * shortest trace to every transition Chromium can bring about from the same walk, plays it against a real row and compares the two after every step. The world is a file of its own because both tests walk it, and they must walk the same one — otherwise the code would be tested against a world the invariants were never
 * checked in. Kept out of this file, it also keeps a model of the browser out of the rules of the row.
 *
 * A change to the rules is therefore made here and nowhere else, and the two tests say at once whether it still holds and whether the shell still follows it. A change to how the row looks is made in the shell alone.
 */

/** Every field a position can be edited in, in the order they stand in on the row. A model position is edited in all of them. */
export const EDITABLE_FIELDS = ['position', 'width', 'height', 'quantity'] as const;

export type EditableField = (typeof EDITABLE_FIELDS)[number];

/** The kinds of position a row can be edited for: a model, measured and counted, and a stock item, counted only. */
export type EditableKind = 'model' | 'stock';

/**
 * The fields each kind is edited in, in the order Tab walks them, which is the order they stand in on the row.
 *
 * A stock item is not made to a size, so it has no width or height to edit — the row shows none for it.
 */
const FIELDS_OF: Readonly<Record<EditableKind, ReadonlyArray<EditableField>>> = { model: EDITABLE_FIELDS, stock: ['position', 'quantity'] };

/** The fields a kind of position is edited in, in the order Tab walks them. */
export function editableFieldsOf(kind: EditableKind): ReadonlyArray<EditableField> {
	return FIELDS_OF[kind];
}

/** Where focus goes when the row goes into edit mode, and where Tab from the row itself lands: the place, which both kinds have first. */
export const FIRST_FIELD: EditableField = 'position';

/**
 * What the user has done to a field since the row went into edit mode.
 *
 * Untouched is a field that shows the item's own value, whatever that is by now. The other three are fields the user has typed in: what they typed is invalid, the same as the item's value, or a change to it.
 */
export type FieldDraft = 'changed' | 'invalid' | 'unchanged' | 'untouched';

export type Draft = Readonly<Record<EditableField, FieldDraft>>;

/** What the row is drawing: a model or a stock position, the two kinds that can be edited, another kind, or nothing. */
export type ItemKind = EditableKind | 'none' | 'other';

/** Whether a row drawing that kind can be edited. */
export function isEditable(item: ItemKind): item is EditableKind {
	return item === 'model' || item === 'stock';
}

/**
 * The row's state. A draft exists only in edit mode, so a row reading its item cannot hold one.
 *
 * `refused` is whether Enter has been refused for an invalid field. The row says so only from then on, so the warning does not come and go while the user is still typing.
 *
 * A draft holds every field, and a stock position's width and height stay untouched in it: the row shows no field for them, so nothing is typed in one.
 */
export type PositionRowState = { readonly item: ItemKind; readonly mode: 'reading' } | { readonly draft: Draft; readonly item: EditableKind; readonly mode: 'editing'; readonly refused: boolean };

/** Where focus went when it left an element inside the row, as the shell reads it off `relatedTarget` and `document.hasFocus()`. */
export type FocusDestination = 'inside' | 'nowhere' | 'outside';

export type PositionRowEvent =
	/** Clicked(): a pointer click, or Space or Enter released while the row itself has focus. */
	| { readonly by: 'enter' | 'pointer' | 'space'; readonly type: 'activated' }
	/** F2 pressed anywhere in the row. */
	| { readonly type: 'editRequested' }
	/** Escape pressed anywhere in the row. */
	| { readonly type: 'escapePressed' }
	/** Enter released in one of the fields. */
	| { readonly type: 'fieldEntered' }
	/** The user changed the text of a field. `differs` is whether a valid text names another value than the item holds. */
	| { readonly differs: boolean; readonly field: EditableField; readonly type: 'fieldTyped'; readonly valid: boolean }
	/** Focus left an element inside the row. */
	| { readonly to: FocusDestination; readonly type: 'focusLeft' }
	/** The row was handed another position, or none: the list re-dealt it. */
	| { readonly item: ItemKind; readonly type: 'itemChanged' }
	/** The row was handed the same position as a new object: a price landed, or the machine wrote to it. `differs` is, for each field, whether what the user typed in it names another value than the replacement holds. */
	| { readonly differs: Readonly<Record<EditableField, boolean>>; readonly type: 'itemReplaced' }
	/** Tab or Shift+Tab pressed anywhere in the row. `from` is the field that has focus, or null when the row itself has. */
	| { readonly backwards: boolean; readonly from: EditableField | null; readonly type: 'tabPressed' };

/** What the shell does after a transition, in this order: move focus, then dispatch. */
export interface PositionRowEffects {
	/** The fields whose changed values are dispatched, or null when nothing is. Never empty. */
	readonly commit: ReadonlyArray<EditableField> | null;
	/** Where focus is moved, or null when it is left where it is. */
	readonly focus: EditableField | 'row' | null;
	/** Whether the key's default action is prevented. */
	readonly preventDefault: boolean;
	/** Whether the row answers as a SelectListItemRenderer does: by toggling its selection. */
	readonly toggleSelection: boolean;
}

export interface PositionRowStep {
	readonly effects: PositionRowEffects;
	readonly state: PositionRowState;
}

/** A draft with nothing typed in it, which is what a row going into edit mode starts with. */
export const UNTOUCHED: Draft = { height: 'untouched', position: 'untouched', quantity: 'untouched', width: 'untouched' };

export const initial: PositionRowState = { item: 'none', mode: 'reading' };

const NOTHING: PositionRowEffects = { commit: null, focus: null, preventDefault: false, toggleSelection: false };

export function transition(state: PositionRowState, event: PositionRowEvent): PositionRowStep {
	return state.mode === 'reading' ? reading(state, event) : editing(state, event);
}

/** Whether the shell shows the fields in place of the labels. */
export function fieldsShown(state: PositionRowState): boolean {
	return state.mode === 'editing';
}

/**
 * Whether the status icon warns that the draft cannot be saved: Enter has been refused, and a field is still invalid.
 *
 * On the row rather than on the field, and as a warning rather than an error: the row's status icon is where it says what is wrong with the position, and an error there means the engine or the API turned something down. Mending the field takes the warning away again, without another Enter.
 */
export function draftWarning(state: PositionRowState): boolean {
	return state.mode === 'editing' && state.refused && EDITABLE_FIELDS.some((field) => state.draft[field] === 'invalid');
}

/** The fields a commit would dispatch: every one the user changed, and only those. */
export function changedFields(draft: Draft): ReadonlyArray<EditableField> {
	return EDITABLE_FIELDS.filter((field) => draft[field] === 'changed');
}

function reading(state: Extract<PositionRowState, { mode: 'reading' }>, event: PositionRowEvent): PositionRowStep {
	switch (event.type) {
		case 'activated':
			// Enter is how a keyboard edits a position that can be edited, so on one it is not a click
			if (event.by === 'enter' && isEditable(state.item)) {
				return startEditing(state.item);
			}
			return { effects: { ...NOTHING, toggleSelection: true }, state };
		case 'editRequested':
			return isEditable(state.item) ? startEditing(state.item) : stay(state);
		case 'itemChanged':
			return stay({ item: event.item, mode: 'reading' });
		case 'escapePressed':
		case 'fieldEntered':
		case 'fieldTyped':
		case 'focusLeft':
		case 'itemReplaced':
		case 'tabPressed':
			return stay(state);
	}
}

function editing(state: Extract<PositionRowState, { mode: 'editing' }>, event: PositionRowEvent): PositionRowStep {
	switch (event.type) {
		case 'activated':
			// Enter anywhere in the row commits, the row itself included; a click or Space in a row
			// being edited is not a selection
			return event.by === 'enter' ? enter(state) : stay(state);
		case 'editRequested':
			return stay(state);
		case 'escapePressed':
			return { effects: { ...NOTHING, focus: 'row' }, state: { item: state.item, mode: 'reading' } };
		case 'fieldEntered':
			return enter(state);
		case 'fieldTyped':
			return stay({ ...state, draft: { ...state.draft, [event.field]: typed(event.valid, event.differs) } });
		case 'focusLeft':
			// only focus that left the row ends the edit. The window losing focus is not the user
			// leaving: the field keeps document.activeElement and gets the focus back
			return event.to === 'outside' ? stay({ item: state.item, mode: 'reading' }) : stay(state);
		case 'itemChanged':
			return stay({ item: event.item, mode: 'reading' });
		case 'itemReplaced':
			return stay({ ...state, draft: compared(state.draft, event.differs) });
		case 'tabPressed':
			return { effects: { ...NOTHING, focus: nextField(state.item, event.from, event.backwards), preventDefault: true }, state };
	}
}

function startEditing(item: EditableKind): PositionRowStep {
	return { effects: { ...NOTHING, focus: FIRST_FIELD }, state: { draft: UNTOUCHED, item, mode: 'editing', refused: false } };
}

function stay(state: PositionRowState): PositionRowStep {
	return { effects: NOTHING, state };
}

/** Enter in a row being edited: refused while a field is invalid, and otherwise the end of the edit, dispatching whatever was changed. */
function enter(state: Extract<PositionRowState, { mode: 'editing' }>): PositionRowStep {
	if (EDITABLE_FIELDS.some((field) => state.draft[field] === 'invalid')) {
		return stay({ ...state, refused: true });
	}
	const changed = changedFields(state.draft);
	return { effects: { ...NOTHING, commit: changed.length === 0 ? null : changed, focus: 'row' }, state: { item: state.item, mode: 'reading' } };
}

function typed(valid: boolean, differs: boolean): FieldDraft {
	if (!valid) {
		return 'invalid';
	}
	return differs ? 'changed' : 'unchanged';
}

/** A replacement leaves untouched fields showing its own values and invalid ones invalid; a valid text is a change or not by what the replacement holds. */
function compared(draft: Draft, differs: Readonly<Record<EditableField, boolean>>): Draft {
	const next = { ...draft };
	for (const field of EDITABLE_FIELDS) {
		if (draft[field] === 'changed' || draft[field] === 'unchanged') {
			next[field] = differs[field] ? 'changed' : 'unchanged';
		}
	}
	return next;
}

/**
 * The field Tab moves to among the kind's own fields: the next one round, or the first or last from the row itself — round the row and never out of it.
 *
 * Worked out from the kind's fields rather than written out field by field, since a model and a stock item walk different rounds and each is read straight off {@link FIELDS_OF}.
 */
function nextField(item: EditableKind, from: EditableField | null, backwards: boolean): EditableField {
	const fields = FIELDS_OF[item];
	const step = backwards ? -1 : 1;
	// from the row itself, Tab lands on the first field and Shift+Tab on the last
	const at = from === null ? (backwards ? 0 : -1) : fields.indexOf(from);
	return fields[(at + step + fields.length) % fields.length] as EditableField;
}
